import { useState, useEffect, useContext } from 'react';
import { Grid, Box, Typography, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SttDialog } from '../../../sporttia/dialogs/SttDialog';
import { AppContext } from '../../../sporttia/AppContext';
import {
    SttFormTime,
    SttSelectWeekdays,
    SttPriceList,
} from '../../../sporttia/all';
import {
    getErrorMessage,
    subsetObject,
    deleteElementFromArray,
    updateElementInArray,
} from '../../../lib/utils';
import { PERMISSIONS } from '../../../lib/permissions';

type FreeSchedule = {
    id: number;
};

type SlotPrice = {
    id: number;
    name: string;
    prices: Array<{ price: number }>;
    price?: number;
};

type Slot = {
    id?: number;
    weekdays: number[];
    ini: string;
    end: string;
    prices?: SlotPrice[];
};

type Props = {
    idSlot?: number | string | null;
    freeSchedule?: FreeSchedule;
    onClose?: () => void;
    onUpdate?: (slot: Slot) => void;
};

/**
 * Free schedule slot
 * ==================
 *
 * Props
 *
 * 	* integer|string idSlot: if it's "create" then start creating process
 * 	* object freeSchedule: only used when creating
 *	* function onClose
 *	* function onUpdate(slot): execute when the slot has changed on server
 */
export default function FreeScheduleSlotDialog({
    idSlot = null,
    freeSchedule,
    onClose,
    onUpdate,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [slot, setSlot] = useState<Slot | null>(null);
    const [creating, setCreating] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    /**
     * Load slot
     */
    const loadSlot = () => {
        cxt?.api('GET', `/frees/schedules/slots/${idSlot}`, {
            success: (r: { slot: Slot }) => {
                setSlot(r.slot);
                setCreating(false);
            },
        });
    };

    /**
     * Save
     */
    const saveSlot = () => {
        setLoading(true);
        const params = subsetObject(slot!, ['ini', 'end', 'weekdays']);

        // Create
        if (creating) {
            cxt?.api('POST', `/frees/schedules/${freeSchedule!.id}/slots`, {
                params,
                success: (r: { slot: Slot }) => {
                    cxt?.showMessage('S', t('Saved'));
                    setSlot(r.slot);
                    setCreating(false);
                    setLoading(false);
                    if (onUpdate) onUpdate(r.slot);
                },
                error: (err: any) => {
                    cxt.showMessage('E', getErrorMessage(err));
                    setLoading(false);
                },
            });

            // Update
        } else {
            cxt?.api('PUT', `/frees/schedules/slots/${slot!.id}`, {
                params,
                success: (r: { slot: Slot }) => {
                    cxt?.showMessage('S', t('Saved'));
                    setLoading(false);
                    if (onUpdate) onUpdate(r.slot);
                    if (onClose) onClose();
                },
                error: (err: any) => {
                    cxt.showMessage('E', getErrorMessage(err));
                    setLoading(false);
                },
            });
        }
    };

    /**
     * Add price to slot
     */
    const addPrice = (price: SlotPrice) => {
        if (price.prices?.[0]) {
            cxt?.api(
                'POST',
                `/frees/schedules/slots/${slot!.id}/prices/${price.id}`,
                {
                    success: () => {
                        const priceAdded: SlotPrice = {
                            id: price.id,
                            name: price.name,
                            price: price.prices[0]?.price,
                            prices: price.prices,
                        };
                        const slotUpdated: Slot = {
                            ...slot!,
                            prices: updateElementInArray(
                                slot!.prices || [],
                                priceAdded,
                            ),
                        };
                        setSlot(slotUpdated);
                        if (onUpdate) onUpdate(slotUpdated);
                    },
                },
            );
        }
    };

    /**
     * Delete price
     */
    const deletePrice = (price: SlotPrice) => {
        cxt?.api(
            'DELETE',
            `/frees/schedules/slots/${slot!.id}/prices/${price.id}`,
            {
                success: () => {
                    const newSlot: Slot = {
                        ...slot!,
                        prices: deleteElementFromArray(
                            slot!.prices || [],
                            price,
                        ),
                    };
                    setSlot(newSlot);
                    if (onUpdate) onUpdate(newSlot);
                },
            },
        );
    };

    /**
     * Handle change slot
     */
    const handleChangeSlot = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: any;
    }) => {
        if (!name) return;
        setSlot({ ...slot!, [name]: value });
    };

    /**
     * Slot id changes
     */
    useEffect(() => {
        if (idSlot) {
            if (idSlot === 'create') {
                setSlot({
                    weekdays: [1, 2, 3, 4, 5, 6, 0],
                    ini: '07:00',
                    end: '23:00',
                });
                setCreating(true);
            } else {
                loadSlot();
            }
        }
    }, [idSlot]);

    // Render
    return (
        <SttDialog
            open={idSlot !== null}
            onClose={onClose}
            title={creating ? t('CreateTimeSlot') : t('Slot')}
            content={
                slot && (
                    <Box>
                        {/* Weekdays Section */}
                        <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            gutterBottom
                        >
                            {t('Weekdays')}
                        </Typography>
                        <Box mb={3}>
                            <SttSelectWeekdays
                                selected={slot.weekdays}
                                onChange={handleChangeSlot}
                            />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Time Range Section */}
                        <Typography
                            variant="subtitle2"
                            color="textSecondary"
                            gutterBottom
                        >
                            {t('TimeRange')}
                        </Typography>
                        <Grid container spacing={2}>
                            <SttFormTime
                                grid
                                md={6}
                                xs={6}
                                sm={6}
                                name="ini"
                                caption={t('From')}
                                defVal={slot.ini}
                                min="00:00:00"
                                max="23:45:00"
                                interval={5}
                                onChange={handleChangeSlot}
                            />

                            <SttFormTime
                                grid
                                md={6}
                                xs={6}
                                sm={6}
                                name="end"
                                caption={t('Until')}
                                defVal={slot.end}
                                min="00:00:00"
                                max="23:50:00"
                                interval={5}
                                onChange={handleChangeSlot}
                            />
                        </Grid>

                        {/* Prices Section */}
                        {!creating && (
                            <>
                                <Divider sx={{ my: 3 }} />
                                <Typography
                                    variant="subtitle2"
                                    color="textSecondary"
                                    gutterBottom
                                >
                                    {t('Rates')}
                                </Typography>
                                <SttPriceList
                                    prices={slot.prices as any}
                                    onAdd={addPrice as any}
                                    onDelete={deletePrice as any}
                                />
                            </>
                        )}
                    </Box>
                )
            }
            buttons={[
                {
                    type: 'save',
                    onClick: () => saveSlot(),
                    permission: PERMISSIONS.SCHEDULE.MODIFY,
                    loading,
                },
                {
                    type: 'close',
                    onClick: onClose,
                },
            ]}
        />
    );
}
